import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    additionalResults?: { [key: string]: string } | null;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // AMR-310
    warehouseLinkReplacementMessage?: TypographyPresentationProps;
    // Sonic
    stockInformationContainer?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin-top: auto;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css``,
    },
    availabilityByWarehouseLinkGridItem: {
        width: 12,
        css: css`
            padding: 5px 0;
        `,
    },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        weight: 400,
        ellipsis: true, // MP 16351: Added display none below
        css: css`
            display: none;
            width: 100%;
            white-space: nowrap;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    availabilityByWarehouseLink: {
        typographyProps: {
            weight: 700,
        },
    },
    // AMR-310
    warehouseLinkReplacementMessage: {
        weight: 700,
    },
    // SONIC
    stockInformationContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: nowrap;
        `,
    },
};

// MP 16351:  display was block
const StockInformationDot = styled.div<{ color: string }>`
    position: relative;
    height: 20px;
    padding-left: 20px;
    display: none;
    border-radius: 100%;
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(1).string()};
    ::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        border: 3px solid #fff;
        border-radius: 100%;
        width: 100%;
        height: 100%;
        transform: scale(0.8);
    }
`;

// MP 16351:  display was flex
const InventoryMessage = styled.div<{ color: string }>`
    display: none;
    align-items: center;
    padding: 5px 20px;
    border-radius: 5px;
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(1).string()};
    ${IconWrapper} {
        margin-right: 5px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    // custom: always 'green'
    return "#67E306";

    // if (!productSettings.showInventoryAvailability) {
    //     return "#67E306";
    // }
    // const availabilityType = availability?.messageType;
    // if (availabilityType === AvailabilityMessageType.OutOfStock) {
    //     return "#D8D8D8";
    // }

    // if (availabilityType === AvailabilityMessageType.LowStock) {
    //     return "#E3A506";
    // }

    // if (availabilityType === AvailabilityMessageType.Available) {
    //     return "#67E306";
    // }

    // return "#D8D8D8";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    // custom: always 'available'
    if (!productSettings.showInventoryAvailability) {
        return Check;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    // custom: always 'Available'.
    const availabilityMessage = translate("Available");

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;

    if (failedToLoadInventory) {
        inventoryTextComponent = (
            <StyledWrapper {...styles.stockInformationContainer}>
                <StockInformationDot color="#D8D8D8"></StockInformationDot>
                <Typography {...styles.messageText} color="common.disabled" ellipsis>
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        inventoryTextComponent = (
            // <InventoryMessage color={color} {...styles.inventoryMessage}>
            //     <Icon color="primary.contrast" src={iconSrc} />
            <StyledWrapper {...styles.stockInformationContainer}>
                <StockInformationDot color={color}></StockInformationDot>
                <Typography
                    {...styles.messageText}
                    color="common.disabled"
                    ellipsis
                    data-test-selector="availabilityMessage"
                >
                    {availabilityMessage /* {availability.message} */}
                </Typography>
            </StyledWrapper>
            // </InventoryMessage>
        );
    } else if (availability) {
        // inventoryTextComponent = <></>;
        inventoryTextComponent = (
            <StyledWrapper {...styles.stockInformationContainer}>
                <StockInformationDot color={color}></StockInformationDot>
                <Typography
                    {...styles.messageText}
                    color="common.disabled"
                    ellipsis
                    data-test-selector="availabilityMessage"
                >
                    <small>{availabilityMessage}</small>
                    {/* <small>No stock information</small> */}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
